import { useCallback, useRef, useState } from 'react';
import type { DataGlueGraph, NodeInstance } from '@glueapp/graph-types';
import { NODE_LIBRARY_DICTIONARY } from '@glueapp/graph-nodes';
import { useAgentSession, type UseAgentSessionReturn } from '../useAgentSession';
import { AGENT_COLORS, type AgentInstance, type NearbyNode, type SpatialContext } from './agentTypes';

// ─── Types ────────────────────────────────────────────────────────────────

export interface AgentManager {
    agents: AgentInstance[];
    spawnAgent: (position: { x: number; y: number }, functionId: string | null) => void;
    removeAgent: (agentId: string) => void;
    updateAgentPosition: (agentId: string, position: { x: number; y: number }) => void;
    toggleAgentExpanded: (agentId: string) => void;
    /** Access the underlying session return for a given agent */
    getAgentSession: (agentId: string) => UseAgentSessionReturn | undefined;
    /** Focus the most recent agent (for TopBar button) */
    focusOrSpawn: (position: { x: number; y: number }, functionId: string | null) => void;
}

interface AgentEntry {
    instance: AgentInstance;
    session: UseAgentSessionReturn;
}

interface UseAgentManagerOptions {
    graphId: string;
    graph: DataGlueGraph | undefined;
    metadata: { name?: string; description?: string } | undefined;
}

// ─── Nearby Node Computation ──────────────────────────────────────────────

const NEARBY_RADIUS = 2000;

function computeNearbyNodes(position: { x: number; y: number }, nodeInstances: NodeInstance[]): NearbyNode[] {
    const nearby: NearbyNode[] = [];
    for (const node of nodeInstances) {
        const deltaX = node.position.x - position.x;
        const deltaY = node.position.y - position.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (distance <= NEARBY_RADIUS) {
            const definition = NODE_LIBRARY_DICTIONARY[node.nodeId];
            nearby.push({
                nodeId: node.id,
                nodeType: node.nodeId,
                name: definition?.name ?? node.nodeId,
                position: { x: node.position.x, y: node.position.y },
                distance: Math.round(distance),
            });
        }
    }
    nearby.sort((nodeA, nodeB) => nodeA.distance - nodeB.distance);
    return nearby;
}

export function computeSpatialContext(
    position: { x: number; y: number },
    functionId: string | null,
    graph: DataGlueGraph | undefined,
): SpatialContext {
    let nodeInstances: NodeInstance[] = [];
    if (graph) {
        if (functionId) {
            const func = graph.functions?.find(matchingFunc => matchingFunc.id === functionId);
            nodeInstances = func?.nodeInstances ?? [];
        } else {
            nodeInstances = graph.nodeInstances;
        }
    }
    return {
        agentPosition: position,
        functionId,
        nearbyNodes: computeNearbyNodes(position, nodeInstances),
    };
}

// ─── Hook ─────────────────────────────────────────────────────────────────

let agentCounter = 0;

/**
 * Manages multiple agent instances. Currently wraps a single useAgentSession
 * since each agent gets its own WebSocket, but stores per-agent UI state
 * (position, color, expanded) independently.
 *
 * For the initial implementation, we support one active agent at a time
 * and derive its AgentInstance from the session state.
 */
export function useAgentManager({ graphId, graph, metadata }: UseAgentManagerOptions): AgentManager {
    const [agentInstances, setAgentInstances] = useState<
        Map<
            string,
            { position: { x: number; y: number }; color: (typeof AGENT_COLORS)[number]; isExpanded: boolean; functionId: string | null }
        >
    >(new Map());
    const colorIndexRef = useRef(0);

    // Single session for now — multi-session support can be added later
    // by creating multiple useAgentWebSocket instances
    const agentSession = useAgentSession({
        graphId,
        graph,
        metadata,
    });

    // Track the active agent ID so we can map session → agent
    const activeAgentIdRef = useRef<string | null>(null);

    const spawnAgent = useCallback(
        (position: { x: number; y: number }, functionId: string | null) => {
            const agentId = `agent-${++agentCounter}-${Date.now()}`;
            const color = AGENT_COLORS[colorIndexRef.current % AGENT_COLORS.length];
            colorIndexRef.current++;

            setAgentInstances(prev => {
                const next = new Map(prev);
                next.set(agentId, { position, color, isExpanded: false, functionId });
                return next;
            });

            activeAgentIdRef.current = agentId;

            // Open the session if not already open
            if (!agentSession.isOpen) {
                agentSession.toggle();
            }
        },
        [agentSession],
    );

    const removeAgent = useCallback(
        (agentId: string) => {
            setAgentInstances(prev => {
                const next = new Map(prev);
                next.delete(agentId);
                return next;
            });

            if (activeAgentIdRef.current === agentId) {
                agentSession.close();
                activeAgentIdRef.current = null;
            }
        },
        [agentSession],
    );

    const updateAgentPosition = useCallback((agentId: string, position: { x: number; y: number }) => {
        setAgentInstances(prev => {
            const entry = prev.get(agentId);
            if (!entry) return prev;
            const next = new Map(prev);
            next.set(agentId, { ...entry, position });
            return next;
        });
    }, []);

    const toggleAgentExpanded = useCallback((agentId: string) => {
        setAgentInstances(prev => {
            const entry = prev.get(agentId);
            if (!entry) return prev;
            const next = new Map(prev);
            next.set(agentId, { ...entry, isExpanded: !entry.isExpanded });
            return next;
        });
    }, []);

    const getAgentSession = useCallback(
        (agentId: string): UseAgentSessionReturn | undefined => {
            if (agentId === activeAgentIdRef.current) {
                return agentSession;
            }
            return undefined;
        },
        [agentSession],
    );

    const focusOrSpawn = useCallback(
        (position: { x: number; y: number }, functionId: string | null) => {
            if (activeAgentIdRef.current && agentInstances.has(activeAgentIdRef.current)) {
                // Focus existing — just toggle expanded
                toggleAgentExpanded(activeAgentIdRef.current);
            } else {
                spawnAgent(position, functionId);
            }
        },
        [agentInstances, spawnAgent, toggleAgentExpanded],
    );

    // Build AgentInstance array from stored UI state + session state
    const agents: AgentInstance[] = [];
    for (const [agentId, uiState] of agentInstances) {
        const isActive = agentId === activeAgentIdRef.current;
        agents.push({
            agentId,
            position: uiState.position,
            color: uiState.color,
            isExpanded: uiState.isExpanded,
            functionId: uiState.functionId,
            sessionId: isActive ? agentSession.sessionId : null,
            sessionStatus: isActive ? agentSession.sessionStatus : 'idle',
            agentStatus: isActive ? agentSession.agentStatus : 'idle',
            connectionState: isActive ? agentSession.connectionState : 'disconnected',
            model: isActive ? agentSession.model : 'sonnet',
            autoApproveRuns: isActive ? agentSession.autoApproveRuns : false,
            debugMode: isActive ? agentSession.debugMode : false,
            entries: isActive ? agentSession.entries : [],
            tokenUsage: isActive ? agentSession.tokenUsage : { totalInput: 0, totalOutput: 0, steps: [] },
            activeNodeId: isActive ? agentSession.activeNodeId : null,
        });
    }

    return {
        agents,
        spawnAgent,
        removeAgent,
        updateAgentPosition,
        toggleAgentExpanded,
        getAgentSession,
        focusOrSpawn,
    };
}
